import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import { maintenanceAPI } from '../utils/api';
import { MaintenanceTemplate, MaintenanceTemplateTask } from '../types';
import { 
  Save, 
  X, 
  Plus, 
  Trash2, 
  ArrowLeft,
  Settings,
  List,
  GripVertical
} from 'lucide-react';

const MaintenanceTemplateManager: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [template, setTemplate] = useState<Partial<MaintenanceTemplate>>({
    name: '',
    description: '',
    service_type: 'routine',
    scheduling_mode: 'whichever_first',
    duration_days: undefined,
    duration_months: undefined,
    run_hours_interval: undefined,
    alert_days_before: 7,
    alert_hours_before: 10,
    is_active: true,
    tasks: [],
  });

  useEffect(() => {
    if (isEdit && id) {
      loadTemplate(parseInt(id));
    }
  }, [id, isEdit]);

  const loadTemplate = async (templateId: number) => {
    try {
      setLoading(true);
      const response = await maintenanceAPI.getTemplate(templateId);
      setTemplate(response.template);
    } catch (error: any) {
      console.error('Error loading template:', error);
      alert('Failed to load template');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      if (isEdit && id) {
        await maintenanceAPI.updateTemplate(parseInt(id), template);
      } else {
        await maintenanceAPI.createTemplate(template);
      }
      
      navigate('/maintenance/templates');
    } catch (error: any) {
      console.error('Error saving template:', error);
      alert(error.response?.data?.error || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const addTask = () => {
    setTemplate({
      ...template,
      tasks: [
        ...(template.tasks || []),
        {
          task_name: '',
          task_description: '',
          sort_order: (template.tasks?.length || 0),
          is_required: true,
        },
      ],
    });
  };

  const updateTask = (index: number, field: keyof MaintenanceTemplateTask, value: any) => {
    const tasks = [...(template.tasks || [])];
    tasks[index] = { ...tasks[index], [field]: value };
    setTemplate({ ...template, tasks });
  };

  const removeTask = (index: number) => {
    const tasks = [...(template.tasks || [])];
    const task = tasks[index];
    if (task.id) {
      // Mark for deletion
      tasks[index] = { ...task, _delete: true };
    } else {
      // Remove new task
      tasks.splice(index, 1);
    }
    setTemplate({ ...template, tasks });
  };

  const validateForm = () => {
    if (!template.name) {
      alert('Template name is required');
      return false;
    }

    if (template.scheduling_mode === 'duration' && !template.duration_days && !template.duration_months) {
      alert('Duration-based scheduling requires either days or months');
      return false;
    }

    if (template.scheduling_mode === 'run_hours' && !template.run_hours_interval) {
      alert('Run hours-based scheduling requires run hours interval');
      return false;
    }

    if (template.scheduling_mode === 'whichever_first') {
      const hasDuration = template.duration_days || template.duration_months;
      const hasRunHours = template.run_hours_interval;
      if (!hasDuration && !hasRunHours) {
        alert('Whichever-first scheduling requires at least one of: duration or run hours');
        return false;
      }
    }

    return true;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading template...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/maintenance/templates')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {isEdit ? 'Edit Template' : 'Create Template'}
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  {isEdit ? 'Update maintenance template' : 'Create a new maintenance template'}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => navigate('/maintenance/templates')}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (validateForm()) handleSave();
                }}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="space-y-6">
          {/* Basic Info */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Template Name *
                </label>
                <input
                  type="text"
                  value={template.name || ''}
                  onChange={(e) => setTemplate({ ...template, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Monthly Service, 500 Hour Service"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={template.description || ''}
                  onChange={(e) => setTemplate({ ...template, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                  placeholder="Describe this maintenance type..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Service Type
                </label>
                <select
                  value={template.service_type || 'routine'}
                  onChange={(e) => setTemplate({ ...template, service_type: e.target.value as any })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="routine">Routine</option>
                  <option value="major">Major</option>
                  <option value="emergency">Emergency</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
          </div>

          {/* Scheduling */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Scheduling</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Scheduling Mode *
                </label>
                <select
                  value={template.scheduling_mode || 'whichever_first'}
                  onChange={(e) => setTemplate({ ...template, scheduling_mode: e.target.value as any })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="duration">Duration-based (time)</option>
                  <option value="run_hours">Run Hours-based</option>
                  <option value="whichever_first">Whichever Comes First</option>
                </select>
              </div>

              {(template.scheduling_mode === 'duration' || template.scheduling_mode === 'whichever_first') && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Duration (Days)
                    </label>
                    <input
                      type="number"
                      value={template.duration_days || ''}
                      onChange={(e) => setTemplate({ ...template, duration_days: e.target.value ? parseInt(e.target.value) : undefined })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="0"
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Duration (Months)
                    </label>
                    <input
                      type="number"
                      value={template.duration_months || ''}
                      onChange={(e) => setTemplate({ ...template, duration_months: e.target.value ? parseInt(e.target.value) : undefined })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="0"
                      min="0"
                    />
                  </div>
                </div>
              )}

              {(template.scheduling_mode === 'run_hours' || template.scheduling_mode === 'whichever_first') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Run Hours Interval
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={template.run_hours_interval || ''}
                    onChange={(e) => setTemplate({ ...template, run_hours_interval: e.target.value ? parseFloat(e.target.value) : undefined })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., 250, 500, 1000"
                    min="0"
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Alert Days Before
                  </label>
                  <input
                    type="number"
                    value={template.alert_days_before || 7}
                    onChange={(e) => setTemplate({ ...template, alert_days_before: parseInt(e.target.value) || 7 })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Alert Hours Before
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={template.alert_hours_before || 10}
                    onChange={(e) => setTemplate({ ...template, alert_hours_before: parseFloat(e.target.value) || 10 })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min="0"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Tasks */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Checklist Tasks</h2>
              <button
                onClick={addTask}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 text-sm"
              >
                <Plus className="w-4 h-4" />
                Add Task
              </button>
            </div>

            <div className="space-y-3">
              {template.tasks?.filter(t => !t._delete).map((task, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="border border-gray-200 rounded-lg p-4"
                >
                  <div className="flex items-start gap-3">
                    <GripVertical className="w-5 h-5 text-gray-400 mt-2" />
                    <div className="flex-1 space-y-3">
                      <div>
                        <input
                          type="text"
                          value={task.task_name || ''}
                          onChange={(e) => updateTask(index, 'task_name', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Task name (e.g., Change oil filter)"
                        />
                      </div>
                      <div>
                        <textarea
                          value={task.task_description || ''}
                          onChange={(e) => updateTask(index, 'task_description', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          rows={2}
                          placeholder="Task description (optional)"
                        />
                      </div>
                      <div className="flex items-center gap-4">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={task.is_required !== false}
                            onChange={(e) => updateTask(index, 'is_required', e.target.checked)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">Required</span>
                        </label>
                        <input
                          type="number"
                          value={task.sort_order || index}
                          onChange={(e) => updateTask(index, 'sort_order', parseInt(e.target.value) || index)}
                          className="w-20 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Order"
                        />
                      </div>
                    </div>
                    <button
                      onClick={() => removeTask(index)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </motion.div>
              ))}

              {(!template.tasks || template.tasks.filter(t => !t._delete).length === 0) && (
                <div className="text-center py-8 text-gray-500">
                  <List className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No tasks added yet. Click "Add Task" to create a checklist.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaintenanceTemplateManager;

